import type {
  IAmenityReservationRepository,
  PaginatedResponse,
  QueryParams,
  CreateReservationInput,
  CreateReservationForUnitInput,
  UpdateReservationInput,
  RefundDepositInput,
} from '@yawi/domain';
import { buildQueryString } from '@yawi/domain';
import type { AmenityReservation, ReservationAmenitySnapshot, Unit } from '@yawi/types';
import { ReservationStatus } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

interface ApiReservationResponse {
  id: string;
  reservationCode?: string | null;
  amenityId: string;
  memberId: string;
  unitId?: string | null;
  memberName?: string;
  member: {
    id: string;
    displayName: string;
    masterUserId?: string;
    unit?: Unit | null;
  };
  amenity?: {
    id: string;
    name: string;
    code?: string | null;
    imageUrl?: string | null;
    timezone?: string | null;
  } | null;
  startAt: string;
  date?: string;
  startTime?: string;
  hours: number;
  totalCost: number;
  depositAmount: number;
  isRefunded: boolean;
  deductionAmount?: number | null;
  deductionReason?: string | null;
  status: 'CONFIRMED' | 'CANCELLED' | 'PENDING';
  serviceChargeId?: string | null;
  depositChargeId?: string | null;
  refundExpenseId?: string | null;
  notes?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt?: string;
}

const deriveLocalFields = (
  startAt: string,
  timezone: string | null | undefined,
): { date: string; startTime: string } => {
  // Use the amenity's timezone when available. Fall back to the device's local
  // timezone (no timeZone option) rather than a hardcoded identifier — avoids
  // the 1-hour offset caused by stale IANA DST rules for America/Mexico_City
  // (Mexico abolished DST in October 2022; outdated runtimes still apply CDT).
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone || 'America/Mexico_City',
  };
  const parts = new Intl.DateTimeFormat('en-CA', options).formatToParts(new Date(startAt));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    startTime: `${hour}:${get('minute')}`,
  };
};

const mapApiReservation = (api: ApiReservationResponse): AmenityReservation => {
  const { date: derivedDate, startTime: derivedTime } = deriveLocalFields(api.startAt, api.amenity?.timezone);
  // Show the explicit local hour that was reserved. Deriving from startAt can
  // shift it when the browser/server timezone differs.
  const date = api.date ?? derivedDate;
  const startTime = api.startTime?.slice(0, 5) ?? derivedTime;

  return {
    id: api.id,
    reservationCode: api.reservationCode ?? null,
    amenityId: api.amenityId,
    memberId: api.memberId,
    unitId: api.unitId ?? null,
    memberName: api.memberName,
    member: api.member,
    amenity: api.amenity as ReservationAmenitySnapshot | undefined,
    date,
    startTime,
    hours: api.hours,
    totalCost: api.totalCost,
    depositAmount: api.depositAmount,
    isRefunded: api.isRefunded,
    deductionAmount: api.deductionAmount ?? null,
    deductionReason: api.deductionReason ?? null,
    status: api.status as ReservationStatus,
    serviceChargeId: api.serviceChargeId ?? null,
    depositChargeId: api.depositChargeId ?? null,
    refundExpenseId: api.refundExpenseId ?? null,
    notes: api.notes ?? null,
    createdBy: api.createdBy ?? null,
    createdByName: api.createdByName ?? null,
    createdAt: api.createdAt,
    updatedAt: api.updatedAt,
  };
};

export class ApiAmenityReservationRepository implements IAmenityReservationRepository {
  async getReservations(params?: QueryParams): Promise<PaginatedResponse<AmenityReservation>> {
    const client = getApiClient();
    const queryString = buildQueryString(params || {});
    const response = await client.get<PaginatedResponse<ApiReservationResponse>>(
      `v1/amenities/reservations${queryString ? `?${queryString}` : ''}`
    );
    return {
      ...response,
      data: response.data.map(mapApiReservation),
    };
  }

  async getReservationById(id: string): Promise<AmenityReservation | null> {
    const client = getApiClient();
    try {
      const api = await client.get<ApiReservationResponse>(`v1/amenities/reservations/${id}`);
      return mapApiReservation(api);
    } catch {
      return null;
    }
  }

  async getMyReservations(params?: QueryParams): Promise<PaginatedResponse<AmenityReservation>> {
    const client = getApiClient();
    const queryString = buildQueryString(params || {});
    const response = await client.get<PaginatedResponse<ApiReservationResponse>>(
      `v1/amenities/reservations/my-reservations${queryString ? `?${queryString}` : ''}`
    );
    return {
      ...response,
      data: response.data.map(mapApiReservation),
    };
  }

  async getUpcomingReservations(): Promise<AmenityReservation[]> {
    const client = getApiClient();
    const response = await client.get<ApiReservationResponse[]>(
      'v1/amenities/reservations/upcoming'
    );
    return response.map(mapApiReservation);
  }

  async createReservation(data: CreateReservationInput): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.post<ApiReservationResponse>('v1/amenities/reservations', data);
    return mapApiReservation(api);
  }

  async createReservationForUnit(
    unitId: string,
    data: CreateReservationForUnitInput,
  ): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.post<ApiReservationResponse>('v1/amenities/reservations/admin', {
      ...data,
      unitId,
    });
    return mapApiReservation(api);
  }

  async updateReservation(id: string, data: UpdateReservationInput): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.put<ApiReservationResponse>(
      `v1/amenities/reservations/${id}`,
      data
    );
    return mapApiReservation(api);
  }

  async cancelReservation(id: string): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.post<ApiReservationResponse>(
      `v1/amenities/reservations/${id}/cancel`
    );
    return mapApiReservation(api);
  }

  async confirmReservation(id: string): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.post<ApiReservationResponse>(
      `v1/amenities/reservations/${id}/confirm`
    );
    return mapApiReservation(api);
  }

  async refundDeposit(id: string, data: RefundDepositInput): Promise<AmenityReservation> {
    const client = getApiClient();
    const api = await client.post<ApiReservationResponse>(
      `v1/amenities/reservations/${id}/refund`,
      data
    );
    return mapApiReservation(api);
  }
}

